import { z } from 'zod';

import type { MemberTransferDataView } from './memberTransferRequest.dto';

/** Maximum members accepted in one bulk transfer request. */
export const MAX_BULK_MEMBER_TRANSFER_SIZE = 100;

/**
 * Create body for a bulk member transfer.
 */
export const CreateBulkMemberTransferRequestSchema = z
  .object({
    memberIds: z
      .array(z.string())
      .min(1, 'Select at least one member')
      .max(
        MAX_BULK_MEMBER_TRANSFER_SIZE,
        `Select no more than ${MAX_BULK_MEMBER_TRANSFER_SIZE} members`
      ),
    originatingGuideId: z.string().nonempty('Required'),
    receivingGuideId: z.string().nonempty('Required'),
    creatorId: z.string().optional(),
    transferReasonId: z.coerce.number(),
    stringDetail: z.string().optional(),
    numericDetail: z.coerce.number().optional().nullable(),
    numericDetailByMemberId: z
      .record(z.string(), z.coerce.number())
      .optional(),
    timestampDetail: z.number().optional().nullable(),
    commentToReceivingGuide: z.string().nonempty('Required'),
    effectiveAt: z.number(),
  })
  .strict();

/**
 * Validated create body for a bulk member transfer.
 */
export type CreateBulkMemberTransferRequestDto = z.infer<
  typeof CreateBulkMemberTransferRequestSchema
>;

/**
 * Portal form values for the bulk transfer dialog.
 */
export const BulkMemberTransferRequestFormSchema = z
  .object({
    memberIds: z.array(z.string()).min(1, 'Select at least one member'),
    originatingGuideId: z.string().nonempty('Required'),
    receivingGuideId: z.string().nonempty('Required'),
    creatorId: z.string().optional(),
    transferReasonId: z.coerce.number().min(1, 'Required'),
    stringDetail: z.string().optional(),
    numericDetail: z.coerce.number().optional().nullable(),
    numericDetailByMemberId: z
      .record(z.string(), z.coerce.number())
      .optional(),
    timestampDetail: z.string().optional().nullable(),
    commentToReceivingGuide: z.string().nonempty('Required'),
  })
  .strict();

/**
 * Validated portal form values for a bulk member transfer.
 */
export type BulkMemberTransferRequestFormType = z.infer<
  typeof BulkMemberTransferRequestFormSchema
>;

/**
 * Response from creating an auto-approved transfer for each selected member.
 */
export interface BulkMemberTransferCreateResponseDto {
  bulkRequestId: number;
  memberTransferDataViews: MemberTransferDataView[];
}
